import React, { forwardRef, useState, useRef, useMemo, useEffect } from 'react';
import { List, CellMeasurerCache, CellMeasurer } from 'react-virtualized';
import Label from '../../../atom/component/Label/Label.js';
import MenuButton from '../../../atom/component/MenuButton/MenuButton.js';
import MenuOption from '../../../atom/component/MenuOption/MenuOption.js';
import Popper from '../../Popper/Popper.js';
import Search from '../Search/Search.js';
import clsx from 'clsx';
import useHAF from '../../../atom/hook/useHAF.js';
import Keys from '../../../atom/constant/keycodes.js';
import '../../../tailwind.css.js';

var options = {
    placement: 'bottom-start',
    modifiers: [
        { name: 'offset', options: { offset: [0, 4] } },
        { name: 'flip', enabled: false },
    ],
};
var newCache = function () { return new CellMeasurerCache({ fixedWidth: true, defaultHeight: 42, minHeight: 42 }); };
var SingleSelect = forwardRef(function (props, _ref) {
    var _a, _b;
    var ref = useHAF(_ref).ref;
    var _c = useState(newCache()), cache = _c[0], setCache = _c[1];
    var _d = useState(), selection = _d[0], setSelection = _d[1];
    var _e = useState(false), open = _e[0], setOpen = _e[1];
    var _f = useState(''), query = _f[0], setQuery = _f[1];
    var _g = useState(336), menuHeight = _g[0], setMenuHeight = _g[1];
    var searchRef = useRef(null);
    var listRef = useRef(null);
    var focused = useRef();
    var items = useMemo(function () {
        if (!query || !props.search) {
            return props.items;
        }
        else if (typeof props.search === 'function') {
            return props.items.filter(function (_a) {
                var item = _a.item;
                return props.search(item, query);
            });
        }
        else {
            return props.items.filter(function (_a) {
                var label = _a.label;
                return label.toLowerCase().includes(query.toLowerCase());
            });
        }
    }, [query, props.search, props.items]);
    var toggleOption = function (ev, option) {
        var _a, _b;
        if (props.deselect && (selection === null || selection === void 0 ? void 0 : selection.key) && (option === null || option === void 0 ? void 0 : option.key) === (selection === null || selection === void 0 ? void 0 : selection.key)) {
            setSelection(undefined);
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, undefined);
        }
        else if (!selection || (option === null || option === void 0 ? void 0 : option.key) !== (selection === null || selection === void 0 ? void 0 : selection.key)) {
            setSelection(option);
            (_b = props.onChange) === null || _b === void 0 ? void 0 : _b.call(props, option.item);
        }
        setOpen(false);
    };
    var onClick = function (ev) {
        var _a;
        setOpen(!open);
        (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props, ev);
    };
    var onBlur = function (ev) {
        var _a, _b, _c;
        if (!ev.relatedTarget || (!((_a = ref.current) === null || _a === void 0 ? void 0 : _a.contains(ev.relatedTarget)) && !((_c = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.nextElementSibling) === null || _c === void 0 ? void 0 : _c.contains(ev.relatedTarget)))) {
            setOpen(false);
        }
    };
    var onSearch = function (_query) {
        var _a;
        (_a = props.onSearch) === null || _a === void 0 ? void 0 : _a.call(props, _query);
        if (props.search) {
            setQuery(_query);
        }
    };
    var onSearchBlur = function (ev) {
        var _a, _b, _c, _d;
        if (ev.type === "click" && (ev.target === searchRef.current && (((_a = ref.current) === null || _a === void 0 ? void 0 : _a.contains(ev.relatedTarget)) || ((_c = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.nextElementSibling) === null || _c === void 0 ? void 0 : _c.contains(ev.relatedTarget))))) {
            focused.current = searchRef.current;
            (_d = focused.current) === null || _d === void 0 ? void 0 : _d.focus();
        }
    };
    var text = function () {
        var classes = clsx({ 'text-sm': props.size === 'sm' });
        switch (props.variant) {
            case 'secondary':
            case 'tertiary':
                return clsx(classes, {
                    'text-disabled-text': props.disabled,
                    'text-blue-301 hover:text-brand-medium active:text-blue-301': !props.disabled && open,
                    'text-brand hover:text-brand-medium active:text-blue-301': !props.disabled && !open,
                });
            case 'quaternary':
                return clsx(classes, {
                    'text-disabled-text': props.disabled,
                    'text-blue-301 hover:text-brand-medium active:text-blue-301': !props.disabled && open,
                    'text-dark-text hover:text-brand-medium active:text-blue-301': !props.disabled && !open,
                });
            default:
                return clsx(classes, {
                    'text-disabled-text': props.disabled,
                    'text-dark-text group-focus:text-blue-301': !props.disabled,
                });
        }
    };
    var renderRow = function (_a) {
        var _b;
        var index = _a.index; _a.key; var style = _a.style, parent = _a.parent;
        var option = items[index] || null;
        var nodeIdx = index; // Here intentionally to distinguish between entries that depend on relative node positions and those that depend on cache entry positions, should row positions shift in the future for any reason
        return !option ? null : (
        // @ts-ignore
        React.createElement(CellMeasurer, { key: nodeIdx, cache: cache, parent: parent, rowIndex: index, columnIndex: 0 },
            React.createElement(MenuOption, { tabIndex: 0, key: nodeIdx, "data-nodeidx": nodeIdx, wrap: props.wrap, disabled: option.disabled, selected: (selection === null || selection === void 0 ? void 0 : selection.key) === option.key, onClick: function (ev) { return toggleOption(ev, option); }, onFocus: function (ev) { focused.current = ev.target; }, children: option.node || option.label, suffix: (_b = props.actions) === null || _b === void 0 ? void 0 : _b.map(function (action) { return action(option.item, option.idx || -1); }), style: style })));
    };
    var keyboardEventSource = function (ev) {
        var _a, _b, _c;
        if ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.contains(ev.target)) {
            return 'button';
        }
        else if ((_c = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.nextElementSibling) === null || _c === void 0 ? void 0 : _c.contains(ev.target)) {
            return 'popper';
        }
        return null;
    };
    var handleButtonKeydown = function (ev) {
        var _a, _b, _c, _d, _e;
        switch (ev.code) {
            case Keys.ESCAPE: {
                if (open) {
                    setOpen(false);
                }
                break;
            }
            case Keys.SPACE: {
                if (ev.target === ref.current) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setOpen(!open);
                }
                break;
            }
            case Keys.ENTER: {
                ev.preventDefault();
                ev.stopPropagation();
                if (!open) {
                    setOpen(true);
                }
                break;
            }
            case Keys.ARROW_DOWN: {
                if (!open) {
                    setOpen(true);
                }
                else if ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.nextElementSibling) {
                    var first = Array.from((_c = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.nextElementSibling) === null || _c === void 0 ? void 0 : _c.querySelectorAll("[data-nodeidx]").values())[0];
                    focused.current = first;
                    (_d = focused.current) === null || _d === void 0 ? void 0 : _d.focus();
                }
                break;
            }
            case Keys.ARROW_UP: {
                if (open) {
                    setOpen(false);
                    focused.current = searchRef.current;
                    (_e = focused.current) === null || _e === void 0 ? void 0 : _e.focus();
                }
                break;
            }
        }
    };
    var handlePopperKeydown = function (ev) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        switch (ev.code) {
            case Keys.ESCAPE: {
                if (open) {
                    setOpen(false);
                }
                break;
            }
            case Keys.ENTER: {
                ev.preventDefault();
                ev.stopPropagation();
                if (((_a = ref.current) === null || _a === void 0 ? void 0 : _a.nextElementSibling) && ((_b = focused.current) === null || _b === void 0 ? void 0 : _b.hasAttribute("data-nodeidx"))) {
                    var idx = Number.parseInt((_c = focused.current) === null || _c === void 0 ? void 0 : _c.getAttribute("data-nodeidx"));
                    var option = items[idx];
                    toggleOption(ev, option);
                    focused.current = ref.current;
                    (_d = focused.current) === null || _d === void 0 ? void 0 : _d.focus();
                }
                break;
            }
            case Keys.ARROW_UP: {
                ev.preventDefault();
                ev.stopPropagation();
                if ((_e = ref.current) === null || _e === void 0 ? void 0 : _e.nextElementSibling) {
                    var nodes = Array.from((_g = (_f = ref.current) === null || _f === void 0 ? void 0 : _f.nextElementSibling) === null || _g === void 0 ? void 0 : _g.querySelectorAll("[data-nodeidx]").values()).reverse();
                    var idx_1 = Number.parseInt((_h = focused.current) === null || _h === void 0 ? void 0 : _h.getAttribute("data-nodeidx"));
                    var previousIdx = nodes.findIndex(function (node) { return Number.parseInt(node.getAttribute("data-nodeidx")) < idx_1; });
                    if (previousIdx > -1) {
                        focused.current = nodes[previousIdx];
                        (_j = focused.current) === null || _j === void 0 ? void 0 : _j.focus();
                    }
                    else if ((props.search || props.onSearch) && focused.current !== searchRef.current) {
                        focused.current = searchRef.current;
                        (_k = focused.current) === null || _k === void 0 ? void 0 : _k.focus();
                    }
                    else {
                        setOpen(false);
                        focused.current = ref.current;
                        (_l = focused.current) === null || _l === void 0 ? void 0 : _l.focus();
                    }
                }
                break;
            }
            case Keys.ARROW_DOWN: {
                ev.preventDefault();
                ev.stopPropagation();
                if ((_m = ref.current) === null || _m === void 0 ? void 0 : _m.nextElementSibling) {
                    var nodes = Array.from((_p = (_o = ref.current) === null || _o === void 0 ? void 0 : _o.nextElementSibling) === null || _p === void 0 ? void 0 : _p.querySelectorAll("[data-nodeidx]").values());
                    var idx_2 = Number.parseInt((_q = focused.current) === null || _q === void 0 ? void 0 : _q.getAttribute("data-nodeidx"));
                    var nextIdx = nodes.findIndex(function (node) { return Number.parseInt(node.getAttribute("data-nodeidx")) > idx_2; });
                    if (nextIdx > -1) {
                        focused.current = nodes[nextIdx];
                        (_r = focused.current) === null || _r === void 0 ? void 0 : _r.focus();
                    }
                    else {
                        var first = nodes[0];
                        focused.current = first;
                        (_s = focused.current) === null || _s === void 0 ? void 0 : _s.focus();
                    }
                }
                break;
            }
        }
    };
    var onKeyDown = function (ev) {
        switch (keyboardEventSource(ev)) {
            case 'button':
                handleButtonKeydown(ev);
                break;
            case 'popper':
                handlePopperKeydown(ev);
                break;
        }
    };
    useEffect(function () {
        setSelection(props.value);
    }, [props.value]);
    useEffect(function () {
        setCache(newCache());
    }, [props.wrap, open, items]);
    useEffect(function () {
        var _a;
        (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.recomputeRowHeights();
        var defaultHeight = 336;
        var baselineHeight = 42 * items.length;
        var calculatedHeight = items
            .map(function (_, index) { return (cache === null || cache === void 0 ? void 0 : cache.rowHeight({ index: index })) || 42; })
            .reduce(function (x, y) { return x + y; }, 0);
        setMenuHeight(8 + Math.min(defaultHeight, Math.max(baselineHeight, calculatedHeight)));
    }, [cache]);
    useEffect(function () {
        var _a, _b;
        if (open) {
            focused.current = searchRef.current;
            (_a = focused.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            onSearch('');
            (_b = props.onClose) === null || _b === void 0 ? void 0 : _b.call(props);
        }
    }, [open]);
    return (React.createElement(React.Fragment, null,
        React.createElement(MenuButton, { ref: ref, id: props.id, name: props.name, open: open, onClick: onClick, onBlur: onBlur, disabled: props.disabled, error: props.error, width: props.width, size: props.size, variant: props.variant, className: props.className, onKeyDown: onKeyDown },
            React.createElement("div", { className: 'flex items-center justify-between w-[calc(100%_-_24px)]' },
                React.createElement(Label, { className: clsx('font-normal truncate', text()), label: (selection === null || selection === void 0 ? void 0 : selection.label) || props.placeholder }))),
        React.createElement(Popper, { anchor: ref, open: !props.disabled && open, onBlur: onBlur, onKeyDown: onKeyDown, options: props.options || options, zIndex: props.zIndex || 10 },
            React.createElement("div", { className: 'h-fit rounded-lg bg-white shadow-md overflow-hidden', style: { width: props.menuWidth || ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 336 } },
                (!!props.search || !!props.onSearch) && React.createElement(Search, { ref: searchRef, placeholder: props.nestedPlaceholder, onSearch: onSearch, onBlur: onSearchBlur }),
                // @ts-ignore
                React.createElement(List, { ref: listRef, width: props.menuWidth || ((_b = ref.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 336, height: props.menuHeight || menuHeight, rowHeight: cache.rowHeight, rowCount: items.length || 0, rowRenderer: renderRow, deferredMeasurementCache: cache, className: "py-1 outline-none" }),
                props.footer && typeof props.footer === 'function'
                    ? props.footer({ close: function () { return setOpen(false); }, setOpen: setOpen })
                    : props.footer))));
});

export { SingleSelect as default };
